import React, { useEffect, useMemo, useState } from 'react';
import { FormControl, Select, MenuItem, CircularProgress } from '@mui/material';

type FilterData = Record<string, string[]>;

// Pagination state for each filter type
type PaginationState = {
  page: number;
  hasMore: boolean;
  isLoading: boolean;
};

interface MdmFilterSelectProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  searchValue: string;
  setSearchValue: (value: string) => void;
  selectedOptions: Record<string, string[]>;
  setSelectedOptions: (options: Record<string, string[]>) => void;
  activeFilterType: string;
  setActiveFilterType: (filterType: string) => void;
  visibleColumns: any[];
  fetchFilterValues: (which: string, q?: string, page?: number, pageSize?: number) => Promise<string[]>;
  onClearAll?: () => void;
}

const MdmFilterSelect: React.FC<MdmFilterSelectProps> = ({
  isOpen,
  setIsOpen,
  searchValue,
  setSearchValue,
  selectedOptions,
  setSelectedOptions,
  activeFilterType,
  setActiveFilterType,
  visibleColumns,
  fetchFilterValues,
  onClearAll,
}) => {
  const [filterData, setFilterData] = useState<FilterData>({});
  const [paginationState, setPaginationState] = useState<Record<string, PaginationState>>({});
  const PAGE_SIZE = 100;

  const leftFilters = useMemo(() => {
    return (visibleColumns || [])
      .filter((c: any) => (c?.tableField || c?.id) && c?.showInTable)
      .map((c: any) => (c.tableField || c.id) as string);
  }, [visibleColumns]);

  // SINGLE useEffect - only fetches page 1 when filter type or search changes
  useEffect(() => {
    if (!activeFilterType) return;
    
    const fetchFirstPage = async () => {
      // Set loading state for page 1
      setPaginationState(prev => ({
        ...prev,
        [activeFilterType]: { page: 1, hasMore: true, isLoading: true }
      }));

      try {
        const items = await fetchFilterValues(activeFilterType, searchValue, 1, PAGE_SIZE);
        setFilterData(prev => ({ ...prev, [activeFilterType]: items }));
        
        // Update pagination state based on results
        setPaginationState(prev => ({
          ...prev,
          [activeFilterType]: {
            page: 1,
            hasMore: items.length === PAGE_SIZE, // If we got exactly PAGE_SIZE items, there might be more
            isLoading: false
          }
        }));
      } catch (_e) {
        // On error, stop loading and disable "Load More"
        setPaginationState(prev => ({
          ...prev,
          [activeFilterType]: { page: 1, hasMore: false, isLoading: false }
        }));
      }
    };

    fetchFirstPage();
  }, [activeFilterType, searchValue, fetchFilterValues, PAGE_SIZE]);

  const capitalizeFirstLetter = (str: string): string => {
    if (!str) return str;
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  // Handler for Load More button - NOT in useEffect, only called on button click
  const handleLoadMore = async () => {
    const currentState = paginationState[activeFilterType];
    if (!currentState || currentState.isLoading || !currentState.hasMore) {
      return; // Already loading or no more data
    }

    const nextPage = currentState.page + 1;

    // Set loading state
    setPaginationState(prev => ({
      ...prev,
      [activeFilterType]: { ...prev[activeFilterType], isLoading: true }
    }));

    try {
      const items = await fetchFilterValues(activeFilterType, searchValue, nextPage, PAGE_SIZE);
      
      // Append new items to existing data
      setFilterData(prev => ({
        ...prev,
        [activeFilterType]: [...(prev[activeFilterType] || []), ...items]
      }));

      // Update pagination state
      setPaginationState(prev => ({
        ...prev,
        [activeFilterType]: {
          page: nextPage,
          hasMore: items.length === PAGE_SIZE,
          isLoading: false
        }
      }));
    } catch (_e) {
      // On error, stop loading
      setPaginationState(prev => ({
        ...prev,
        [activeFilterType]: { ...prev[activeFilterType], isLoading: false, hasMore: false }
      }));
    }
  };

  const clearAllFilters = () => {
    const clearedOptions: Record<string, string[]> = {};
    leftFilters.forEach(filterType => {
      clearedOptions[filterType] = [];
    });
    setSelectedOptions(clearedOptions);
    setSearchValue('');
    setActiveFilterType('');
    setPaginationState({}); // Reset pagination state
    setFilterData({}); // Clear filter data
    
    // Call the parent's clear callback to refresh data
    if (onClearAll) {
      onClearAll();
    }
  };

  const hasActiveFilters = () => {
    return Object.values(selectedOptions).some(options => options.length > 0) || !!searchValue;
  };

  const getTotalSelectedCount = () => {
    return Object.values(selectedOptions).reduce((total, options) => total + options.length, 0);
  };

  const getFilteredOptions = (filterType: string) => {
    const options = filterData[filterType] || [];
    return options.filter(option =>
      searchValue ? option.toLowerCase().includes(searchValue.toLowerCase()) : true
    );
  };

  // FIX: Toggle checkbox properly (allow unselect)
  const handleCheckboxChange = (option: string) => {
    const currentSelected = selectedOptions[activeFilterType] || [];
    let newSelected: string[];
    
    if (currentSelected.includes(option)) {
      // Unselect - remove from array
      newSelected = currentSelected.filter(item => item !== option);
    } else {
      // Select - replace with just this option (single select behavior)
      // If you want multi-select, use: newSelected = [...currentSelected, option];
      newSelected = [option];
    }
    
    setSelectedOptions({ ...selectedOptions, [activeFilterType]: newSelected });
  };

  return (
    <FormControl className="manage_secondary_orders_filter" onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}>
      <span style={{ fontSize: '14px', color: '#6E6969' }}>Filter:</span>
      <Select
        variant="outlined"
        labelId="mdm_dropdown_label"
        open={isOpen}
        value={"Filters"}
        onOpen={() => {
          setIsOpen(true);
          setSearchValue('');
        }}
        onClose={() => {
          setIsOpen(false);
          setActiveFilterType(''); // Reset active filter when closing
          setSearchValue(''); // Clear search value
        }}
        renderValue={() => (
          <span style={{ color: '#909090', fontSize: '14px' }}>
            Filters {getTotalSelectedCount() > 0 }
          </span>
        )}
        sx={{
          minWidth: '190px',
          marginLeft: '10px',
          '& .MuiSelect-select': {
            height: '32px',
            padding: '6px 12px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            color: '#909090',
            outline: 'none',
            border: 'none',
          },
          '& .MuiOutlinedInput-root.Mui-focused': {
            outline: 'none',
            boxShadow: 'none',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#DEDEDE',
            borderWidth: '1px',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#DEDEDE',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#DEDEDE',
            borderWidth: '1px',
          },
        }}
        MenuProps={{
          anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
          transformOrigin: { vertical: 'top', horizontal: 'right' },
          PaperProps: { 
            sx: { 
              boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)', 
              marginTop: '4px', 
              border: '1px solid #DEDEDE',
              borderRadius: '8px',
              minWidth: '580px',
              maxHeight: '420px'
            } 
          },
          MenuListProps: { 
            sx: { 
              paddingTop: '0', 
              paddingBottom: '0',
              overflow: 'hidden'
            } 
          },
        }}
      >
        <MenuItem
          key="mdm-filter-menu-item"
          disableRipple
          style={{ 
            padding: 0, 
            backgroundColor: 'transparent', 
            outline: 'none',
            display: 'block'
          }}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            setIsOpen(true);
          }}
        >
          <div 
            className="MSO_filter-dropdown-container" 
            onClick={(e) => { 
              e.stopPropagation(); 
              setIsOpen(true); 
            }}
            style={{
              width: '580px',
              height: '420px',
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: '#fff'
            }}
          >
            <div 
              className="MSO_filter-header"
              style={{
                padding: '16px 20px',
                borderBottom: '1px solid #E5E5E5',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexShrink: 0
              }}
            >
              <span 
                className="MSO_filter-name"
                style={{
                  fontSize: '16px',
                  fontWeight: 600,
                  color: '#333'
                }}
              >
                All Filters
              </span>
              <span 
                className={`MSO_filter-clear-all ${hasActiveFilters() && 'MSO_filter-clear-all_active'}`}
                onClick={(e) => {
                  e.stopPropagation();
                  clearAllFilters();
                }}
                style={{
                  fontSize: '14px',
                  color: hasActiveFilters() ? '#237C9D' : '#999',
                  cursor: hasActiveFilters() ? 'pointer' : 'not-allowed',
                  fontWeight: 500
                }}
              >
                Clear All
              </span>
            </div>

            <div 
              className="MSO_filter_searchBar_container"
              style={{
                padding: '12px 20px',
                borderBottom: '1px solid #E5E5E5',
                flexShrink: 0
              }}
            >
              <input
                className="MSO_fiter_searchBar"
                placeholder="Search..."
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                autoFocus
                onBlur={(e) => e.target.focus()}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #DEDEDE',
                  borderRadius: '6px',
                  fontSize: '14px',
                  outline: 'none'
                }}
              />
            </div>

            <div 
              className="MSO_filter-options"
              style={{
                display: 'flex',
                flex: 1,
                minHeight: 0,
                height: 0
              }}
            >
              <div 
                className="MSO_filter-options-left"
                style={{
                  width: '180px',
                  borderRight: '1px solid #E5E5E5',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  flexShrink: 0,
                  backgroundColor: '#FAFAFA',
                  maxHeight: '100%'
                }}
              >
                {leftFilters.map((filterType) => (
                  <div
                    key={filterType}
                    className={`${activeFilterType === filterType && 'MSO_filterSelected'}`}
                    onClick={() => setActiveFilterType(filterType)}
                    style={{
                      padding: '12px 20px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      color: activeFilterType === filterType ? '#237C9D' : '#444',
                      backgroundColor: activeFilterType === filterType ? '#E8F4F8' : 'transparent',
                      borderLeft: activeFilterType === filterType ? '3px solid #237C9D' : '3px solid transparent',
                      fontWeight: activeFilterType === filterType ? 600 : 400,
                      transition: 'all 0.2s ease',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}
                  >
                    {capitalizeFirstLetter(filterType)} ({selectedOptions[filterType]?.length || 0})
                  </div>
                ))}
              </div>

              <div 
                className="MSO_filter-options-right"
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  minWidth: 0,
                  overflow: 'hidden'
                }}
              >
                <div 
                  className="MSO_filter-option" 
                  style={{ 
                    flex: 1,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    padding: '12px 20px'
                  }}
                >
                  {getFilteredOptions(activeFilterType).length > 0 ? (
                    <>
                      {getFilteredOptions(activeFilterType).map((option) => (
                        <label
                          key={option}
                          style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '10px', 
                            color: '#444444', 
                            fontSize: '14px',
                            padding: '8px 0',
                            cursor: 'pointer',
                            wordBreak: 'break-word'
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedOptions[activeFilterType]?.includes(option) || false}
                            onChange={() => handleCheckboxChange(option)}
                            style={{ 
                              borderColor: '#CBCBCB',
                              cursor: 'pointer',
                              flexShrink: 0,
                              width: '16px',
                              height: '16px'
                            }}
                          />
                          <span style={{ flex: 1 }}>
                            {capitalizeFirstLetter(option)}
                          </span>
                        </label>
                      ))}

                      {/* Load More Button */}
                      {paginationState[activeFilterType]?.hasMore && 
                       !paginationState[activeFilterType]?.isLoading && (
                        <div style={{ 
                          display: 'flex',
                          justifyContent: 'center',
                          padding: '16px 0',
                          marginTop: '12px',
                          borderTop: '1px solid #E5E5E5'
                        }}>
                          <button
                            onClick={handleLoadMore}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              padding: '8px 16px',
                              backgroundColor: '#237C9D',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '13px',
                              fontWeight: 500,
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#1e6b7d';
                              e.currentTarget.style.transform = 'translateY(-1px)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = '#237C9D';
                              e.currentTarget.style.transform = 'translateY(0)';
                            }}
                          >
                            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                            </svg>
                            Load More
                          </button>
                        </div>
                      )}

                      {/* Loading Indicator */}
                      {paginationState[activeFilterType]?.isLoading && (
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'center', 
                          alignItems: 'center',
                          padding: '16px 0',
                          gap: '8px',
                          marginTop: '12px',
                          borderTop: '1px solid #E5E5E5'
                        }}>
                          <CircularProgress size={18} sx={{ color: '#237C9D' }} />
                          <span style={{ fontSize: '13px', color: '#666' }}>
                            Loading...
                          </span>
                        </div>
                      )}

                      {/* End of List */}
                      {!paginationState[activeFilterType]?.hasMore && 
                       !paginationState[activeFilterType]?.isLoading &&
                       paginationState[activeFilterType]?.page > 1 && (
                        <div style={{ 
                          textAlign: 'center', 
                          color: '#999', 
                          fontSize: '12px',
                          padding: '12px 0',
                          marginTop: '12px',
                          borderTop: '1px solid #E5E5E5'
                        }}>
                          All items loaded
                        </div>
                      )}
                    </>
                  ) : paginationState[activeFilterType]?.isLoading ? (
                    <div style={{ 
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center', 
                      alignItems: 'center',
                      padding: '40px 20px',
                      gap: '12px'
                    }}>
                      <CircularProgress size={28} sx={{ color: '#237C9D' }} />
                      <span style={{ fontSize: '14px', color: '#666' }}>
                        Loading filters...
                      </span>
                    </div>
                  ) : (
                    <div style={{ 
                      textAlign: 'center', 
                      color: '#999', 
                      fontSize: '14px',
                      padding: '40px 20px'
                    }}>
                      No options available
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </MenuItem>
      </Select>
    </FormControl>
  );
};

export default MdmFilterSelect;